import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { MAX_MATCH_JOKERS } from '../../../constants/joker.constants';
import {
  getMatchJokerCost,
  GROUP_STAGE_ROUND,
} from '../../../constants/match-round.constants';
import { PollAiSuggestionResult } from '../../../models/poll-ai-suggestion.model';
import { AuthService } from '../../../services/auth.service';
import { GroupsService } from '../../../services/groups.service';
import { MatchesService } from '../../../services/matches.service';
import { PollsService } from '../../../services/polls.service';
import { StandingsService } from '../../../services/standings.service';
import { UtilsService } from '../../../services/utils.service';
import { UsersListModalComponent } from '../users-list-modal/users-list-modal.component';

interface MatchScoringPoints {
  base: number;
  exact: number;
}

interface MatchPointsBreakdownItem {
  label: string;
  points: number;
  matched: boolean;
}

interface MatchPointsBreakdown {
  items: MatchPointsBreakdownItem[];
  total: number;
}

@Component({
  selector: 'app-matches-list',
  templateUrl: './matches-list.component.html',
  styleUrls: ['./matches-list.component.scss'],
  standalone: false,
})
export class MatchesListComponent implements OnInit {
  @Input() mode: string;
  @Input() user: any;
  @Output() roundChanged: EventEmitter<any> = new EventEmitter<any>();
  // @ViewChildren('input') inputs: QueryList<any>;

  matches: any[];
  rounds: any[];
  roundMatches: any[];
  roundSelected: string = GROUP_STAGE_ROUND;
  currentTimestamp: number;
  isAdmin: boolean;
  dataLoaded$: EventEmitter<any>;
  roundResults: any;
  isMe: boolean;
  groups: any[] = [];
  positionByUserId: Record<string, number> = {};

  private polls: any[];

  constructor(
    private bottomSheet: MatBottomSheet,
    private router: Router,
    private matchesService: MatchesService,
    private authService: AuthService,
    private groupsService: GroupsService,
    private pollsService: PollsService,
    private standingsService: StandingsService,
    private utilsService: UtilsService,
  ) {
    this.currentTimestamp = new Date().getTime() / 1000;
    this.isAdmin = this.authService.isAdmin();
  }

  // initInputs() {
  //   this.inputs.forEach((input, index, inputsArray) => {
  //     input.nativeElement.addEventListener('input', () => {
  //       if (
  //         input.nativeElement.value.length === input.nativeElement.maxLength
  //       ) {
  //         const nextInput = inputsArray[index + 1];

  //         if (nextInput) {
  //           nextInput.nativeElement.focus();
  //           this.scrollToInput(nextInput.nativeElement, 100); // Adjust 20 pixels as margin
  //         }
  //       }
  //     });

  //     input.nativeElement.addEventListener('focus', () => {
  //       this.scrollToInput(input.nativeElement, 100);
  //     });
  //   });
  // }

  // private scrollToInput(element: HTMLElement, margin: number): void {
  //   element.scrollIntoView({
  //     behavior: 'smooth',
  //     block: 'start',
  //   });

  //   // Adjust the scroll position to include the desired margin
  //   setTimeout(() => {
  //     const scrollContainer = this.getScrollContainer(element);
  //     if (scrollContainer) {
  //       scrollContainer.scrollTop -= margin;
  //     }
  //   }, 300); // Adjust timeout as necessary to wait for the scroll to complete
  // }

  // private getScrollContainer(element: HTMLElement): HTMLElement | null {
  //   let parent = element.parentElement;
  //   while (parent) {
  //     const overflowY = window.getComputedStyle(parent).overflowY;
  //     if (overflowY === 'auto' || overflowY === 'scroll') {
  //       return parent;
  //     }
  //     parent = parent.parentElement;
  //   }
  //   return null;
  // }

  async ngOnInit() {
    const loggedUser = this.authService.getUser();
    if (!this.user) {
      this.user = loggedUser;
    }
    this.isMe = !!loggedUser && !!this.user && this.user._id === loggedUser._id;

    const groups = await this.groupsService.getList();
    this.groups = groups.filter((group: any) =>
      loggedUser?.groups?.includes(group._id),
    );
    this.rounds = await this.matchesService.getRounds();
    await this.loadPositions();
    this.roundSelected = this.resolveActiveRound(this.rounds);
    const results = await this.matchesService.getList();
    this.roundResults = results.filter(
      (match: any) => match.round === this.roundSelected,
    );
    await this.loadMatches();
    this.roundMatches = this.matches.filter(
      (match) => match.round === this.roundSelected,
    );

    await this.loadPolls();
    this.roundChanged.emit(this.roundSelected);
    // this.initInputs();
  }

  private resolveActiveRound(rounds: any[]): string {
    if (!rounds?.length) {
      return GROUP_STAGE_ROUND;
    }

    const now = this.currentTimestamp;
    const started = rounds.filter((r) => r.iniTimestamp <= now);
    if (!started.length) {
      return rounds[0].name;
    }

    const active = started.reduce((prev, curr) =>
      curr.iniTimestamp > prev.iniTimestamp ? curr : prev,
    );
    return active.name;
  }

  async selectRound(round: any) {
    this.roundSelected = round.name;
    this.roundMatches = this.matches.filter(
      (match) => match.round === this.roundSelected,
    );
    this.roundChanged.emit(this.roundSelected);
    const results = await this.matchesService.getList();
    this.roundResults = results.filter(
      (match: any) => match.round === this.roundSelected,
    );
  }

  private async loadMatches() {
    let matches = await this.matchesService.getList();
    matches = this.addUIProperties(matches);

    if (this.mode === 'poll') {
      matches = matches.map((match: any) => {
        match.homeGoals = null;
        match.awayGoals = null;
        match.penaltiesWinner = null;

        return match;
      });
    } else {
      matches = matches.map((match: any) => {
        if (match.penaltiesWinner) {
          if (match.penaltiesWinner._id === match.homeTeam?._id) {
            match.homeTeam.checked = true;
            if (match.awayTeam) {
              match.awayTeam.checked = false;
            }
          } else if (match.penaltiesWinner._id === match.awayTeam?._id) {
            if (match.homeTeam) {
              match.homeTeam.checked = false;
            }
            match.awayTeam.checked = true;
          }
        }

        return match;
      });
    }

    this.matches = matches;
    // this.initInputs();
  }

  private async loadPolls() {
    this.polls = await this.pollsService.getList();
    this.matches = this.utilsService.addUIPollsPropertiesToMatches(
      this.matches,
      this.polls,
      this.user,
    );
  }

  private addUIProperties(matches: any[]) {
    matches.map((match: any) => {
      match.roundIniTimestamp = this.rounds.find(
        (round) => round.name === match.round,
      ).iniTimestamp;
      match.showOptions = false;
      match.showPointsBreakdown = false;
      match.pointsBreakdown = null;

      return match;
    });

    return matches;
  }

  readonly MAX_JOKERS = MAX_MATCH_JOKERS;
  readonly jokerSlots = Array.from(
    { length: MAX_MATCH_JOKERS },
    (_, index) => index + 1,
  );

  get jokersUsed(): number {
    if (!this.matches) return 0;
    return this.matches.reduce((acc: number, m: any) => {
      return m.useJoker ? acc + this.jokerCost(m) : acc;
    }, 0);
  }

  get pendingRoundMatchesCount(): number {
    return this.getPendingRoundMatches().length;
  }

  get pendingRoundMatchesLabel(): string {
    const pendingCount = this.pendingRoundMatchesCount;
    const matchesLabel = pendingCount === 1 ? 'partido' : 'partidos';
    return `${pendingCount} ${matchesLabel} por cumplimentar`;
  }

  get clearableMatchesCount(): number {
    if (!this.roundMatches?.length) {
      return 0;
    }

    return this.roundMatches.filter((match: any) => this.canClearMatch(match))
      .length;
  }

  getPendingRoundMatches(): any[] {
    if (!this.roundMatches?.length) {
      return [];
    }

    return this.roundMatches.filter((match: any) => this.isMatchPending(match));
  }

  scrollToLastPlayedMatch(): void {
    const lastPlayed = this.findLastPlayedMatch();
    if (!lastPlayed) {
      return;
    }

    setTimeout(() => {
      const el = document.getElementById(`match-${lastPlayed._id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }

  private findLastPlayedMatch(): any | null {
    if (!this.roundMatches?.length) {
      return null;
    }

    let lastIndex = -1;
    for (let i = 0; i < this.roundMatches.length; i++) {
      if (this.hasResult(this.roundResults, i)) {
        lastIndex = i;
      }
    }

    return lastIndex >= 0 ? this.roundMatches[lastIndex] : null;
  }

  applySuggestedResults(results: PollAiSuggestionResult[]): number {
    if (!this.matches?.length || !results?.length) {
      return 0;
    }

    const resultsByMatchId = new Map(
      results.map((result) => [result.match._id, result]),
    );

    return this.matches.reduce((count: number, match: any) => {
      const result = resultsByMatchId.get(match._id);

      if (!result || !this.isMatchPending(match)) {
        return count;
      }

      this.applySuggestedResult(match, result);
      return count + 1;
    }, 0);
  }

  clearEditableResults(): number {
    if (!this.roundMatches?.length) {
      return 0;
    }

    return this.roundMatches.reduce((count: number, match: any) => {
      if (!this.canClearMatch(match)) {
        return count;
      }

      this.clearMatchPrediction(match);
      return count + 1;
    }, 0);
  }

  jokerCost(match: any): number {
    return getMatchJokerCost(match.round);
  }

  private isMatchPending(match: any): boolean {
    if (!this.hasCompletedScore(match)) {
      return true;
    }

    return (
      this.requiresPenaltiesWinner(match) && !this.hasPenaltiesWinner(match)
    );
  }

  private hasCompletedScore(match: any): boolean {
    return [match.homeGoals, match.awayGoals].every(
      (score) => score !== null && score !== undefined && score !== '',
    );
  }

  private requiresPenaltiesWinner(match: any): boolean {
    return (
      match.round !== GROUP_STAGE_ROUND &&
      match.homeGoals === match.awayGoals &&
      match.homeGoals !== null &&
      match.homeGoals !== undefined
    );
  }

  private hasPenaltiesWinner(match: any): boolean {
    return !!(
      match.penaltiesWinner ||
      match.homeTeam?.checked ||
      match.awayTeam?.checked
    );
  }

  private canClearMatch(match: any): boolean {
    return this.isJokerEditable(match) && this.hasPredictionToClear(match);
  }

  private hasPredictionToClear(match: any): boolean {
    return (
      match.homeGoals !== null ||
      match.awayGoals !== null ||
      this.hasPenaltiesWinner(match) ||
      !!match.useJoker
    );
  }

  private clearMatchPrediction(match: any): void {
    match.homeGoals = null;
    match.awayGoals = null;
    match.penaltiesWinner = null;
    if (match.homeTeam) {
      match.homeTeam.checked = false;
    }
    if (match.awayTeam) {
      match.awayTeam.checked = false;
    }
    match.useJoker = false;
  }

  private applySuggestedResult(
    match: any,
    result: PollAiSuggestionResult,
  ): void {
    match.homeGoals = result.homeGoals;
    match.awayGoals = result.awayGoals;
    this.syncPenaltySelection(match, this.getPenaltyWinnerId(result));
  }

  private syncPenaltySelection(
    match: any,
    penaltiesWinnerId: string | null,
  ): void {
    const requiresWinner =
      match.round !== GROUP_STAGE_ROUND &&
      match.homeGoals !== null &&
      match.homeGoals === match.awayGoals;

    match.penaltiesWinner = penaltiesWinnerId;
    if (match.homeTeam) {
      match.homeTeam.checked =
        requiresWinner && penaltiesWinnerId === match.homeTeam._id;
    }
    if (match.awayTeam) {
      match.awayTeam.checked =
        requiresWinner && penaltiesWinnerId === match.awayTeam._id;
    }
  }

  private getPenaltyWinnerId(result: PollAiSuggestionResult): string | null {
    if (!result.penaltiesWinner) {
      return null;
    }

    return typeof result.penaltiesWinner === 'string'
      ? result.penaltiesWinner
      : result.penaltiesWinner._id;
  }

  isJokerEditable(match: any): boolean {
    return (
      this.isAdmin ||
      (this.isMe &&
        this.mode !== 'results' &&
        match.roundIniTimestamp >= this.currentTimestamp)
    );
  }

  toggleJoker(match: any): void {
    if (!this.isJokerEditable(match)) return;
    const cost = this.jokerCost(match);
    if (!match.useJoker && this.jokersUsed + cost > this.MAX_JOKERS) return;
    match.useJoker = !match.useJoker;
  }

  penaltiesChange(match: any, team: number) {
    if (!match.homeTeam || !match.awayTeam) {
      return;
    }

    if (team === 1) {
      match.homeTeam.checked = !match.homeTeam.checked;
      match.awayTeam.checked = false;
    } else {
      match.awayTeam.checked = !match.awayTeam.checked;
      match.homeTeam.checked = false;
    }
  }

  showStatUsers(event: { title: string; users: any[] }): void {
    this.bottomSheet
      .open(UsersListModalComponent, {
        data: {
          title: event.title,
          users: event.users,
          groups: this.groups,
          positionByUserId: this.positionByUserId,
        },
        panelClass: 'participants-sheet',
      })
      .afterDismissed()
      .subscribe((selectedUser) => {
        if (selectedUser?._id) {
          this.router.navigate([`/home/${selectedUser._id}`]);
        }
      });
  }

  private async loadPositions(): Promise<void> {
    const standings: any = await this.standingsService
      .getSummary()
      .catch(() => null);
    this.positionByUserId = this.buildPositionByUserId(
      standings?.currentStandings ?? [],
    );
  }

  private buildPositionByUserId(standings: any[]): Record<string, number> {
    return standings.reduce((positions, standing, index) => {
      if (standing?.user?._id) {
        positions[standing.user._id] = index + 1;
      }

      return positions;
    }, {});
  }

  togglePointsBreakdown(match: any, result: any): void {
    match.showPointsBreakdown = !match.showPointsBreakdown;
    match.pointsBreakdown = match.showPointsBreakdown
      ? this.buildPointsBreakdown(match, result)
      : null;
  }

  getMatchScoreSummary(match: any): string {
    if (
      !match ||
      !this.isValidScore(match.homeGoals) ||
      !this.isValidScore(match.awayGoals)
    ) {
      return '-';
    }

    const outcomeLabel = this.getMatchOutcomeLabel(match);
    const score = `${match.homeGoals} - ${match.awayGoals}`;
    return outcomeLabel ? `${score} · ${outcomeLabel}` : score;
  }

  private buildPointsBreakdown(match: any, result: any): MatchPointsBreakdown {
    const scoring = this.getScoringPoints(match);
    const hasCorrectOutcome = this.hasCorrectOutcome(match, result);
    const hasExactScore = this.hasExactScorePoints(match, result);
    const subtotal = this.getSubtotal(
      scoring,
      hasCorrectOutcome,
      hasExactScore,
    );
    return {
      items: this.buildBreakdownItems(
        match,
        scoring,
        hasCorrectOutcome,
        hasExactScore,
        subtotal,
      ),
      total: match.useJoker ? subtotal * 2 : subtotal,
    };
  }

  private getScoringPoints(match: any): MatchScoringPoints {
    const roundMultiplier = this.getRoundMultiplier(match);

    return {
      base: 3 * roundMultiplier,
      exact: 2 * roundMultiplier,
    };
  }

  private getRoundMultiplier(match: any): number {
    return this.jokerCost(match);
  }

  private hasCorrectOutcome(match: any, result: any): boolean {
    if (!match || !result) {
      return false;
    }

    const actualHomeGoals = result.homeGoals;
    const actualAwayGoals = result.awayGoals;
    const predictedHomeGoals = match.homeGoals;
    const predictedAwayGoals = match.awayGoals;

    if (
      !this.isValidScore(actualHomeGoals) ||
      !this.isValidScore(actualAwayGoals) ||
      !this.isValidScore(predictedHomeGoals) ||
      !this.isValidScore(predictedAwayGoals)
    ) {
      return false;
    }

    const actualTie = actualHomeGoals === actualAwayGoals;
    const predictedTie = predictedHomeGoals === predictedAwayGoals;

    if (match.round === GROUP_STAGE_ROUND) {
      return (
        (actualHomeGoals > actualAwayGoals &&
          predictedHomeGoals > predictedAwayGoals) ||
        (actualAwayGoals > actualHomeGoals &&
          predictedAwayGoals > predictedHomeGoals) ||
        (actualTie && predictedTie)
      );
    }

    return (
      this.getQualifiedTeamId(result, actualHomeGoals, actualAwayGoals) ===
      this.getQualifiedTeamId(match, predictedHomeGoals, predictedAwayGoals)
    );
  }

  private hasExactScore(match: any, result: any): boolean {
    return (
      match.homeGoals === result?.homeGoals &&
      match.awayGoals === result?.awayGoals
    );
  }

  private hasExactScorePoints(match: any, result: any): boolean {
    return this.hasExactScore(match, result);
  }

  private getSubtotal(
    scoring: MatchScoringPoints,
    hasCorrectOutcome: boolean,
    hasExactScore: boolean,
  ): number {
    return (
      (hasCorrectOutcome ? scoring.base : 0) +
      (hasExactScore ? scoring.exact : 0)
    );
  }

  private buildBreakdownItems(
    match: any,
    scoring: MatchScoringPoints,
    hasCorrectOutcome: boolean,
    hasExactScore: boolean,
    subtotal: number,
  ): MatchPointsBreakdownItem[] {
    const items: MatchPointsBreakdownItem[] = [
      {
        label: this.getBasePointsLabel(match),
        points: hasCorrectOutcome ? scoring.base : 0,
        matched: hasCorrectOutcome,
      },
      {
        label: 'Marcador exacto',
        points: hasExactScore ? scoring.exact : 0,
        matched: hasExactScore,
      },
    ];

    if (match.useJoker) {
      items.push({
        label: 'Joker activo',
        points: subtotal,
        matched: subtotal > 0,
      });
    }

    return items;
  }

  private getBasePointsLabel(match: any): string {
    const multiplierLabel = this.getRoundMultiplierLabel(match);
    const suffix = multiplierLabel ? ` · ${multiplierLabel}` : '';

    return `Pronostico acertado (1X2${suffix})`;
  }

  private getRoundMultiplierLabel(match: any): string {
    const multiplier = this.getRoundMultiplier(match);
    return multiplier > 1 ? `x${multiplier}` : '';
  }

  private getMatchOutcomeLabel(match: any): string {
    const outcome = this.getMatchOutcome(match);
    if (!outcome) return '';
    if (outcome === 'DRAW') return 'Empate';

    const isHomeWinner = outcome === match.homeTeam?._id;
    const teamName = isHomeWinner ? match.homeTeam?.name : match.awayTeam?.name;
    if (match.round === GROUP_STAGE_ROUND) {
      return `Gana ${teamName}`;
    }

    const resolutionLabel = this.getKnockoutResolutionLabel(match);
    return resolutionLabel
      ? `Pasa ${teamName} en ${resolutionLabel}`
      : `Pasa ${teamName}`;
  }

  private getMatchOutcome(match: any): string | null {
    if (!match) return null;
    if (match.homeGoals > match.awayGoals) return match.homeTeam?._id ?? null;
    if (match.awayGoals > match.homeGoals) return match.awayTeam?._id ?? null;
    if (match.round === GROUP_STAGE_ROUND) return 'DRAW';
    return this.getWinnerId(match.penaltiesWinner);
  }

  private getWinnerId(winner: any): string | null {
    if (!winner) return null;
    return typeof winner === 'string' ? winner : (winner._id ?? null);
  }

  private getKnockoutResolutionLabel(match: any): string {
    if (this.getWinnerId(match.penaltiesWinner)) {
      return 'penaltis';
    }

    if (
      this.isValidScore(match.homeExtraTimeGoals) &&
      this.isValidScore(match.awayExtraTimeGoals) &&
      match.homeExtraTimeGoals !== match.awayExtraTimeGoals
    ) {
      return 'prórroga';
    }

    return '';
  }

  private isValidScore(score: any): boolean {
    return typeof score === 'number' && !Number.isNaN(score);
  }

  private getQualifiedTeamId(
    match: any,
    homeGoals: number,
    awayGoals: number,
  ): string | null {
    if (homeGoals > awayGoals) return match.homeTeam?._id ?? null;
    if (awayGoals > homeGoals) return match.awayTeam?._id ?? null;
    return this.getWinnerId(match.penaltiesWinner);
  }

  hasRoundStarted(match: any): boolean {
    return !!match && match.roundIniTimestamp <= this.currentTimestamp;
  }

  hasResult(roundResults: any[], index: number): boolean {
    const result = roundResults?.[index];
    return result?.homeGoals !== null && result?.homeGoals !== undefined;
  }

  onScoreInput(
    match: any,
    scoreField: 'homeGoals' | 'awayGoals',
    event: Event,
  ): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input?.value ?? '';
    const sanitizedValue = rawValue.replace(/\D+/g, '');

    input.value = sanitizedValue;
    match[scoreField] =
      sanitizedValue === '' ? null : Number.parseInt(sanitizedValue, 10);
  }

  hasInvalidScoresForSubmit(): boolean {
    if (!this.matches || this.matches.length === 0) {
      return false;
    }

    return this.matches.some(
      (match: any) =>
        !this.isValidScoreValue(match.homeGoals) ||
        !this.isValidScoreValue(match.awayGoals),
    );
  }

  private isValidScoreValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }

    if (typeof value === 'number') {
      return Number.isInteger(value) && value >= 0;
    }

    if (typeof value === 'string') {
      return /^\d+$/.test(value.trim());
    }

    return false;
  }

  private normalizeScoreValue(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isInteger(value) && value >= 0 ? value : null;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (!/^\d+$/.test(trimmedValue)) {
        return null;
      }

      return Number.parseInt(trimmedValue, 10);
    }

    return null;
  }

  getResults() {
    return this.matches.map((match: any) => {
      match.homeGoals = this.normalizeScoreValue(match.homeGoals);
      match.awayGoals = this.normalizeScoreValue(match.awayGoals);

      if (match.round !== GROUP_STAGE_ROUND) {
        if (match.homeTeam?.checked) {
          match.penaltiesWinner = match.homeTeam._id;
        } else if (match.awayTeam?.checked) {
          match.penaltiesWinner = match.awayTeam._id;
        } else {
          match.penaltiesWinner = null;
        }
      } else {
        match.penaltiesWinner = null;
      }

      match.match = { _id: match._id };

      delete match.homeTeam?.checked;
      delete match.awayTeam?.checked;
      // delete match._id;

      return match;
    });
  }
}
